import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Badge, Tabs, Dialog, Drawer, Alert, Card, ConfirmDialog, Skeleton, SegmentedControl, useToast } from '@dreamtree-org/twreact-ui';
import { Eye, Monitor, Smartphone, History, Rocket, ExternalLink, Sparkles, Pencil } from 'lucide-react';
import { validateStore } from '@dreamtree-org/landing-kit/schema';
import { PageHeader, EmptyState } from '@/components';
import { useMediaQuery, usePhone } from '@/lib/useMediaQuery';
import { useAppApi } from '@/lib/api';
import { errorText, formatDate } from '@/lib/format';
import { useQuery } from '@/lib/useQuery';
import { useCan } from '@rbac/useCan';
import { storeDocOf } from '@/storefront/lib/design';
import { STUDIO_PANELS } from '../components/appearance/areas';
import PreviewFrame from '../components/appearance/PreviewFrame';
import PublishCheckDialog from '../components/appearance/PublishCheckDialog';
import { OPTION_TILE } from '../components/appearance/optionTile';
import CustomHomeStudio from '../components/appearance/CustomHomeStudio';
import { errorsBySection, STUDIO_AREAS, errorsByArea, areaProblemCount } from '../lib/appearance';

const SAVE_AFTER_MS = 1500;

/** A failed draft write as editor save state: 409 → conflict, otherwise an error with the server's field details. */
function saveFailure(ex) {
  const body = (ex.response && ex.response.data) || {};
  if (ex.response && ex.response.status === 409) return { state: 'conflict', errors: [], message: body.error || 'Someone else changed this draft.' };
  return { state: 'error', errors: body.details || [], message: body.error || errorText(ex) };
}

/**
 * Storefront Studio (D24–D27, B15 D64–D67): the store's landing page, listing and
 * product page templates, header and footer, product card and brand kit — one
 * store document. Edits go to a draft (autosaved, versioned with a lock);
 * "Preview" opens the storefront showing the draft; "Publish" runs the
 * pre-publish check (MERCH-55: placeholders, broken links, missing images…)
 * and makes it live, after "Publish anyway" when the check found something;
 * History rolls back; a starter kit replaces the draft's design in one step.
 * Forms come from landing-kit's registries, and the preview renders with the
 * same components shoppers see, inside an iframe at a real phone or desktop width
 * (D51). Below lg the editor and the preview are two views of one screen
 * (Edit | Preview), so the preview gets the whole phone.
 */
export default function Appearance() {
  const { fetchApi } = useAppApi();
  const { toast } = useToast();
  const can = useCan();
  const editable = can('store', 'appearance');
  // The custom HTML home (ADR 0007) needs its own permission; appearance never implies it.
  const customCode = can('store', 'custom_code');
  const [mode, setMode] = useState('sections'); // 'sections' | 'custom'
  const [draft, setDraft] = useState(null); // server copy: { id, version, lock_version, … }
  const [doc, setDoc] = useState(null); // the store document (landing-kit StoreDoc v2)
  const [theme, setTheme] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [save, setSave] = useState({ state: 'saved', errors: [], message: '' }); // saved | dirty | saving | error | conflict
  const [tab, setTab] = useState(0);
  const [kitsOpen, setKitsOpen] = useState(false);
  const [kits, setKits] = useState(null);
  const [kitPick, setKitPick] = useState(null);
  const [device, setDevice] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 640 ? 'phone' : 'desktop'));
  const [view, setView] = useState('edit'); // below lg: 'edit' | 'preview'
  const wide = useMediaQuery('(min-width: 1024px)');
  const phone = usePhone();
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [warnings, setWarnings] = useState(null); // pre-publish QA findings awaiting "Publish anyway"
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState('');
  const lock = useRef(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const r = await fetchApi('appearanceDraft', {}, false);
      setDraft(r.data);
      setDoc(storeDocOf(r.data));
      setTheme(r.data.theme);
      lock.current = r.data.lock_version;
      setSave({ state: 'saved', errors: [], message: '' });
    } catch (ex) {
      setLoadError(errorText(ex));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Once: fetchApi changes identity every render, and a reload would drop unsaved edits.
  useEffect(() => {
    load();
  }, [load]);

  const persist = useCallback(async (nextDoc, nextTheme) => {
    const local = validateStore(nextDoc);
    if (!local.ok) {
      setSave({ state: 'error', errors: local.errors, message: 'Some fields need attention before this can be saved.' });
      return false;
    }
    setSave((s) => ({ ...s, state: 'saving' }));
    try {
      const r = await fetchApi('appearanceSave', { doc: nextDoc, theme: nextTheme, lock_version: lock.current }, false);
      lock.current = r.data.lock_version;
      setDraft((d) => ({ ...d, ...r.data }));
      setSave({ state: 'saved', errors: [], message: '' });
      return true;
    } catch (ex) {
      setSave(saveFailure(ex));
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave a moment after the last change.
  const change = (nextDoc, nextTheme) => {
    setDoc(nextDoc);
    setTheme(nextTheme);
    if (!editable) return;
    setSave((s) => (s.state === 'conflict' ? s : { ...s, state: 'dirty' }));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(nextDoc, nextTheme), SAVE_AFTER_MS);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const flush = async () => {
    clearTimeout(timer.current);
    if (save.state === 'dirty' || save.state === 'error') return persist(doc, theme);
    return save.state !== 'conflict';
  };

  const openPreview = async () => {
    if (!(await flush())) return;
    try {
      const r = await fetchApi('appearancePreviewLink', {}, false);
      window.open(previewUrl(r.data.token), '_blank', 'noopener');
    } catch (ex) {
      toast.error('Preview link failed', errorText(ex));
    }
  };

  // Publish: save, run the pre-publish check, then confirm (nothing found) or list the findings.
  const startPublish = async () => {
    setBusy('check');
    try {
      if (!(await flush())) return;
      const r = await fetchApi('appearanceQa', {}, false);
      const found = (r.data && r.data.warnings) || [];
      if (found.length) setWarnings(found);
      else setConfirmPublish(true);
    } catch (ex) {
      toast.error('The pre-publish check failed', errorText(ex));
    } finally {
      setBusy('');
    }
  };

  const publish = async (acknowledge = false) => {
    setBusy('publish');
    try {
      if (!(await flush())) return;
      const r = await fetchApi('appearancePublish', { lock_version: lock.current, acknowledge_warnings: acknowledge }, false);
      toast.success(`Version ${r.data.version} is live`, 'Shoppers see the new page now.');
      setConfirmPublish(false);
      setWarnings(null);
      await load();
    } catch (ex) {
      const body = (ex.response && ex.response.data) || {};
      setConfirmPublish(false);
      // The draft changed between the check and publishing: show what the server found now.
      if (body.code === 'QA_WARNINGS' && Array.isArray(body.details) && body.details.length) setWarnings(body.details);
      else {
        setWarnings(null);
        setSave({ ...saveFailure(ex), state: 'error' });
      }
    } finally {
      setBusy('');
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistory(null);
    try {
      setHistory((await fetchApi('appearanceHistory', {}, false)).data);
    } catch (ex) {
      setHistory([]);
      toast.error('Could not load history', errorText(ex));
    }
  };
  const rollback = async (version) => {
    setBusy(`rb${version}`);
    try {
      const r = await fetchApi('appearanceRollback', { version }, false);
      toast.success(`Version ${version} is live again`, `Published as version ${r.data.version}. Your draft is unchanged.`);
      setHistory((await fetchApi('appearanceHistory', {}, false)).data);
    } catch (ex) {
      toast.error('Rollback failed', errorText(ex));
    } finally {
      setBusy('');
    }
  };

  const page = doc ? doc.pages.home : null;
  const area = STUDIO_AREAS[tab].value;
  const { Panel: AreaPanel } = STUDIO_PANELS[area];
  const byArea = useMemo(() => (doc ? errorsByArea(doc, save.errors) : {}), [doc, save.errors]);
  const bySection = useMemo(() => (page ? errorsBySection(page, byArea.home) : {}), [page, byArea]);
  // The live preview renders the store's own published listings and product types.
  const catalog = useQuery('storefront', { limit: 24 });
  const registry = useQuery('productTypes', undefined, { select: (r) => r });
  const previewListings = useMemo(() => (catalog.data && catalog.data.data) || [], [catalog.data]);
  const previewCatalog = useMemo(() => ({
    listings: previewListings,
    types: (registry.data && registry.data.types) || [],
    categories: (catalog.data && catalog.data.categories) || [],
    name: (catalog.data && catalog.data.store && catalog.data.store.name) || 'Your store',
  }), [previewListings, registry.data, catalog.data]);
  const previewUrl = (token) => {
    const path = STUDIO_PANELS[area].path(previewListings[0] && previewListings[0].id);
    return `${path}${path.includes('?') ? '&' : '?'}preview=${encodeURIComponent(token)}`;
  };

  const openKits = async () => {
    setKitsOpen(true);
    if (kits) return;
    try {
      setKits((await fetchApi('appearanceKits', {}, false)).data);
    } catch (ex) {
      setKits([]);
      toast.error('Could not load the starter kits', errorText(ex));
    }
  };
  const applyKit = async () => {
    setBusy('kit');
    try {
      clearTimeout(timer.current);
      await fetchApi('appearanceApplyKit', { kit: kitPick.key, lock_version: lock.current }, false);
      toast.success(`${kitPick.label} applied to your draft`, 'Preview it, then publish when you are happy.');
      setKitPick(null);
      setKitsOpen(false);
      await load();
    } catch (ex) {
      const failed = saveFailure(ex);
      if (failed.state === 'conflict') setSave(failed);
      else toast.error('The starter kit was not applied', failed.message);
      setKitPick(null);
    } finally {
      setBusy('');
    }
  };

  if (loadError) return <EmptyState title="The page editor could not load">{loadError}</EmptyState>;
  if (!doc || !theme) return <Skeleton height={480} />;

  const modeSwitch = customCode ? (
    <SegmentedControl
      aria-label="Home page mode"
      value={mode}
      onChange={setMode}
      options={[{ value: 'sections', label: 'Sections' }, { value: 'custom', label: 'Custom HTML' }]}
    />
  ) : null;
  if (mode === 'custom' && customCode) {
    return (
      <div className="space-y-4">
        <PageHeader title="Storefront Studio" subtitle="Your own HTML home page, served at your store address" />
        {modeSwitch}
        <CustomHomeStudio />
      </div>
    );
  }

  const status = { saved: ['success', 'Draft saved'], dirty: ['default', 'Unsaved changes'], saving: ['default', 'Saving…'], error: ['error', 'Not saved'], conflict: ['warning', 'Changed elsewhere'] }[save.state];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Storefront Studio"
        subtitle="Design your home, listing and product pages, header, footer, product card and brand"
        actions={(
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Badge variant={status[0]} className="mr-auto sm:mr-0" aria-live="polite">{status[1]}</Badge>
            <Button variant="outline" leftIcon={<Sparkles className="h-4 w-4" />} onClick={openKits} disabled={!editable} aria-label="Starter kits">
              <span className="hidden sm:inline">Starter kits</span>
            </Button>
            <Button variant="outline" leftIcon={<History className="h-4 w-4" />} onClick={openHistory} aria-label="History">
              <span className="hidden sm:inline">History</span>
            </Button>
            <Button variant="outline" leftIcon={<Eye className="h-4 w-4" />} onClick={openPreview} disabled={!editable} aria-label="Preview on the storefront">
              <span className="hidden sm:inline">Preview</span>
            </Button>
            <Button leftIcon={<Rocket className="h-4 w-4" />} onClick={startPublish} loading={busy === 'check'} disabled={!editable || save.state === 'conflict'}>Publish</Button>
          </div>
        )}
      />

      {modeSwitch}
      {!editable ? <Alert variant="info" title="View only">Changing the storefront needs the Store setup → appearance permission.</Alert> : null}
      {save.state === 'conflict' ? (
        <Alert variant="warning" title="This draft was changed somewhere else">
          {save.message} <Button size="sm" variant="outline" onClick={load}>Reload the draft</Button>
        </Alert>
      ) : null}
      {save.state === 'error' ? (
        <Alert variant="error" title={save.message || 'Not saved'}>
          {bySection._page || byArea.other.length ? (
            <ul className="list-disc pl-5">{[...(bySection._page || []), ...byArea.other].map((e) => <li key={e.path + e.message}>{e.path} {e.message}</li>)}</ul>
          ) : `Problems are marked in: ${STUDIO_AREAS.filter((a) => areaProblemCount(byArea, a.value)).map((a) => a.label).join(', ') || 'the editor'}.`}
        </Alert>
      ) : null}

      <SegmentedControl
        className="lg:hidden"
        fullWidth
        aria-label="Editor view"
        value={view}
        onChange={setView}
        options={[{ value: 'edit', label: 'Edit', icon: <Pencil className="h-4 w-4" aria-hidden="true" /> }, { value: 'preview', label: 'Preview', icon: <Eye className="h-4 w-4" aria-hidden="true" /> }]}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        <Card className={`space-y-4 p-4 ${view === 'edit' ? '' : 'hidden lg:block'}`}>
          <Tabs index={tab} onChange={setTab} variant="line" orientation="horizontal" animated={false}>
            <Tabs.List className="overflow-x-auto border-b border-[var(--app-border)]">
              {STUDIO_AREAS.map((a) => {
                const n = areaProblemCount(byArea, a.value);
                return (
                  <Tabs.Tab key={a.value} tabClassName="min-h-[44px] whitespace-nowrap">
                    {a.label}
                    {n ? <span className="ml-1.5"><Badge size="xs" variant="error">{n}</Badge></span> : null}
                  </Tabs.Tab>
                );
              })}
            </Tabs.List>
          </Tabs>
          <AreaPanel doc={doc} theme={theme} onDoc={(d) => change(d, theme)} onTheme={(t) => change(doc, t)} byArea={byArea} saveErrors={save.errors} editable={editable} draftId={draft && draft.id} />
        </Card>

        <Card className={`space-y-3 p-3 ${view === 'preview' ? '' : 'hidden lg:block'}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold">Preview</span>
            <SegmentedControl
              size="sm"
              aria-label="Preview size"
              value={device}
              onChange={setDevice}
              options={[{ value: 'desktop', label: 'Desktop', icon: <Monitor className="h-4 w-4" aria-hidden="true" /> }, { value: 'phone', label: 'Phone', icon: <Smartphone className="h-4 w-4" aria-hidden="true" /> }]}
            />
          </div>
          {wide || view === 'preview' ? <PreviewFrame area={area} doc={doc} theme={theme} catalog={previewCatalog} device={device} height={phone ? 600 : 760} /> : null}
          <p className="text-xs opacity-60">{STUDIO_PANELS[area].caption}</p>
        </Card>
      </div>

      <Dialog isOpen={kitsOpen && !kitPick} onClose={() => setKitsOpen(false)} title="Start from a starter kit" size="lg">
        <p className="mb-3 text-sm opacity-80">A complete design for your kind of store: home page, listing and product templates, header, footer and product card. Your colours, logo and fonts stay as they are.</p>
        {!kits ? <Skeleton count={3} /> : (
          <div className="grid gap-2 sm:grid-cols-3">
            {kits.map((k) => (
              <button key={k.key} type="button" className={OPTION_TILE} onClick={() => setKitPick(k)}>
                <span className="block text-sm font-semibold">{k.label}</span>
                <span className="block text-xs opacity-70">{k.description}</span>
              </button>
            ))}
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        isOpen={!!kitPick}
        title={kitPick ? `Use the ${kitPick.label} kit?` : ''}
        description="This replaces your draft's pages, header, footer and product card. Your live storefront doesn't change until you publish, and History keeps every published version."
        confirmLabel="Replace my draft"
        loading={busy === 'kit'}
        onCancel={() => setKitPick(null)}
        onConfirm={applyKit}
      />

      <ConfirmDialog
        isOpen={confirmPublish}
        title="Publish your storefront?"
        description="Shoppers will see this version straight away. You can roll back from History."
        confirmLabel="Publish"
        loading={busy === 'publish'}
        onCancel={() => setConfirmPublish(false)}
        onConfirm={() => publish(false)}
      />

      <PublishCheckDialog warnings={warnings} doc={doc} loading={busy === 'publish'} onBack={() => setWarnings(null)} onPublish={() => publish(true)} />

      <Drawer isOpen={historyOpen} onClose={() => setHistoryOpen(false)} title="Published versions" description="Put an earlier version live again; your draft stays as it is." placement={phone ? 'bottom' : 'right'} size={phone ? 'lg' : 'md'} swipeToClose>
        {!history ? <Skeleton count={4} /> : history.length ? (
          <ul className="divide-y divide-[var(--app-border)]">
            {history.map((h) => (
              <li key={h.version} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <div className="text-sm font-medium">Version {h.version} {h.status === 'published' ? <Badge variant="success" size="sm">Live</Badge> : null}</div>
                  <div className="text-xs opacity-60">{formatDate(h.published_at)} · {h.sections} sections</div>
                </div>
                {h.status === 'published' ? (
                  <Link to="/" target="_blank" className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline" reloadDocument>Open <ExternalLink className="h-3.5 w-3.5" /></Link>
                ) : (
                  <Button size="sm" variant="outline" loading={busy === `rb${h.version}`} disabled={!editable} onClick={() => rollback(h.version)}>Make live</Button>
                )}
              </li>
            ))}
          </ul>
        ) : <p className="text-sm opacity-70">Nothing published yet.</p>}
      </Drawer>
    </div>
  );
}
